import React, { useEffect, useMemo, useState } from "react";
import BaseModal from "../modals/BaseModal";
import Button from "../forms/Button";
import Input from "../forms/Input";
import { boardTaskService, apiErrorMessage } from "../../api/taskBoardService";
import { PLANNING_FIELDS, PLANNING_LABELS, PRIORITIES, STATUS_LABELS } from "./constants";
import { editableTaskFields } from "./boardState";
import { toDateInput } from "./shared";
import MultiSelect from "./MultiSelect";

const fieldClass = "input-field w-full";
const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";
const idsOf = (list) => (Array.isArray(list) ? list.map(idOf).filter(Boolean) : []);
const isEmpty = (v) => (Array.isArray(v) ? v.length === 0 : !v);

const Field = ({ label, error, optional, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-sm font-medium text-gray-700 dark:text-light-200">
      {label}
      {optional ? <span className="text-gray-400 ml-1 font-normal">(optional)</span> : <span className="text-red-500 ml-1">*</span>}
    </span>
    {children}
    {error && <span className="text-sm text-red-500">{error}</span>}
  </label>
);

/**
 * Plan a task out of Product Backlog (or complete missing planning data).
 * Squad, sprint, assignee, due date and priority are all required; they are
 * saved together with the target status in ONE request, so the task only moves
 * once everything is valid. Cancelling leaves the task where it was.
 */
const PlanTaskModal = ({ task, toStatus, position, options, onClose, onSaved, onConflict }) => {
  const { access } = options;
  const editable = useMemo(() => editableTaskFields(access, task), [access, task]);
  const [form, setForm] = useState(() => ({
    squad: idOf(task.squad),
    sprint: idOf(task.sprint),
    assignees: idsOf(task.assignees),
    dueDate: toDateInput(task.dueDate),
    priority: task.priority || "",
  }));
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => setServerError(""), [task._id]);

  const squad = options.squads.find((s) => s._id === form.squad);
  // Squad members plus high_potit members (who need no squad membership).
  const assigneeOptions = useMemo(
    () => [...new Map([...(squad?.members || []).map((m) => m.employee), ...(options.admins || [])].filter(Boolean).map((e) => [e._id, e])).values()],
    [squad, options.admins],
  );
  const sprintOptions = options.sprints.filter((s) => s.status !== "completed" || s._id === form.sprint);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => {
      const next = { ...f, [key]: value };
      // A new squad drops assignees who are not its members.
      if (key === "squad") {
        const s = options.squads.find((x) => x._id === value);
        const members = new Set([...(s?.members || []).map((m) => idOf(m.employee)), ...(options.admins || []).map(idOf)]);
        next.assignees = f.assignees.filter((id) => members.has(id));
      }
      return next;
    });
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e?.preventDefault();
    const er = {};
    PLANNING_FIELDS.forEach((f) => {
      if (isEmpty(form[f])) er[f] = `${PLANNING_LABELS[f]} is required`;
    });
    setErrors(er);
    if (Object.keys(er).length) return;

    // Send only what changed (a lead completing an older task may not change
    // squad/sprint) plus the target status, with the version for conflicts.
    const values = { ...form, dueDate: new Date(form.dueDate).toISOString() };
    const fields = ["squad", ...PLANNING_FIELDS, "assignees"]; // the squad is optional but still saved when chosen
    const original = Object.fromEntries(fields.map((f) => [f, idOf(task[f])]));
    original.assignees = idsOf(task.assignees);
    original.dueDate = task.dueDate ? new Date(task.dueDate).toISOString() : "";
    const differs = (f) =>
      f === "assignees"
        ? [...values.assignees].sort().join() !== [...original.assignees].sort().join()
        : values[f] !== original[f];
    const changes = Object.fromEntries(fields.filter(differs).map((f) => [f, values[f]]));

    setSaving(true);
    setServerError("");
    try {
      const res = await boardTaskService.update(task._id, {
        ...changes,
        status: toStatus,
        // Land where the card was dropped.
        ...(position === undefined ? {} : { position }),
        version: task.version,
      });
      onSaved(res.data);
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.current) {
        onConflict?.(err.response.data.current);
        setServerError("Someone else changed this task. Review the values and try again.");
      } else {
        setServerError(apiErrorMessage(err, "Could not move the task"));
      }
    } finally {
      setSaving(false);
    }
  };

  const locked = (f) => !editable.has(f);

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      size="lg"
      title={`Plan task #${task.number}`}
      subtitle={`Moving “${task.title}” to ${STATUS_LABELS[toStatus]}. Squad and assignees are optional. Without a squad, only high_potit members can be assigned.`}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? "Saving…" : `Move to ${STATUS_LABELS[toStatus]}`}
          </Button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {serverError && (
          <div role="alert" className="text-sm p-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {serverError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Squad" error={errors.squad} optional>
            <select value={form.squad} onChange={set("squad")} disabled={locked("squad")} className={fieldClass} autoFocus>
              <option value="">No squad (high_potit members only)</option>
              {options.squads.map((s) => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Sprint" error={errors.sprint}>
            <select value={form.sprint} onChange={set("sprint")} disabled={locked("sprint")} className={fieldClass}>
              <option value="">Select sprint…</option>
              {sprintOptions.map((s) => (
                <option key={s._id} value={s._id}>{`${s.name} (${s.status})`}</option>
              ))}
            </select>
          </Field>

          <Field label="Assignees" optional>
            <MultiSelect
              ariaLabel="Assignees"
              placeholder={form.squad ? "Select assignees…" : "Select high_potit members (no squad)"}
              value={form.assignees}
              onChange={set("assignees")}
              disabled={locked("assignees")}
              options={assigneeOptions.map((e) => ({ value: e._id, label: e.fullNameEn || e.nickname }))}
            />
            {form.squad && !(squad?.members || []).length && (
              <span className="text-xs text-gray-500 dark:text-light-500">This squad has no members yet.</span>
            )}
          </Field>

          <Field label="Priority" error={errors.priority}>
            <select value={form.priority} onChange={set("priority")} disabled={locked("priority")} className={fieldClass}>
              <option value="">Select priority…</option>
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </Field>

          <Input
            label="Due date"
            type="date"
            required
            value={form.dueDate}
            onChange={set("dueDate")}
            error={errors.dueDate}
            disabled={locked("dueDate")}
          />
        </div>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </BaseModal>
  );
};

export default PlanTaskModal;
